// services/wrapped/renderer.ts
// Desenhista ÚNICO do LibriTui Wrapped: o player do app, o vídeo baixado e as imagens de cada cena
// usam esta mesma função, por isso o que se vê no app é exatamente o que vai para o arquivo.
import React from 'react';
import { Brain, BookOpenText, PenLine, Mic2, BookOpen, Headphones, Mic, WholeWord, Clock, Flame, Crosshair, Trophy, Sparkles, CalendarDays, Library } from 'lucide-react';
import type { SfxName } from './synth';

export const W = 1080;
export const H = 1920;

export type WrappedPeriod = 'daily' | 'weekly' | 'monthly' | 'yearly';
export type SkillKey = 'reading' | 'flashcards' | 'active_voice' | 'writing' | 'pronunciation' | 'listening';

export interface WrappedData {
  username: string;
  displayName?: string;
  avatarUrl?: string;
  period: WrappedPeriod;
  studyDays: number;
  periodDays: number;
  wordsLearned: number;
  totalVocab: number;
  minutes: number;
  voiceMinutes: number;
  pages: number;
  cards: number;
  writing: number;
  pronunciation: number;
  quizzes: number;
  listening: number;
  accuracy: number;
  consistency: number;
  skills: { key: SkillKey; name: string; pct: number }[];
  masteredWords: string[];
}

export interface WrappedAssets {
  avatar: HTMLImageElement | null;
  icons: Record<string, HTMLImageElement>;
}

// ── Paleta fixa do vídeo (grafite quente + pastéis), igual em qualquer tema do app ──
const C = {
  bg: '#1C2430', card: '#252F3E', ink: '#E6E0D6', dark: '#151B24', muted: '#A9B4C4', primary: '#E8823F',
};
type Tint = 'sage' | 'lavender' | 'peach' | 'butter' | 'sky' | 'rose';
const LIGHT: Record<Tint, string> = { sage: '#a3ba9e', lavender: '#aca4d4', peach: '#e3ad8d', butter: '#dfc883', sky: '#97b8cc', rose: '#d9a6b1' };
const DEEP: Record<Tint, string> = { sage: '#2b3429', lavender: '#2e2a3c', peach: '#3b2d25', butter: '#383322', sky: '#243038', rose: '#38292e' };

// ── Cenas ─────────────────────────────────────────────────────────
export type SceneId = 'intro' | 'days' | 'words' | 'time' | 'activities' | 'skills' | 'quality' | 'mastery' | 'outro';
interface SceneDef { id: SceneId; dur: number; tint: Tint; title: string }

const SCENE_DEFS: SceneDef[] = [
  { id: 'intro', dur: 3.2, tint: 'lavender', title: 'Abertura' },
  { id: 'days', dur: 3.2, tint: 'peach', title: 'Constância' },
  { id: 'words', dur: 3.4, tint: 'sky', title: 'Vocabulário' },
  { id: 'time', dur: 3.4, tint: 'sage', title: 'Tempo de prática' },
  { id: 'activities', dur: 4.0, tint: 'butter', title: 'Atividades' },
  { id: 'skills', dur: 3.6, tint: 'lavender', title: 'Habilidades' },
  { id: 'quality', dur: 3.2, tint: 'rose', title: 'Qualidade' },
  { id: 'mastery', dur: 2.8, tint: 'peach', title: 'Maestria' },
  { id: 'outro', dur: 3.6, tint: 'sage', title: 'Encerramento' },
];

export const SCENES = (() => {
  let s = 0;
  return SCENE_DEFS.map(d => { const o = { ...d, start: s }; s += d.dur; return o; });
})();
export const TOTAL_DURATION = SCENES.reduce((a, s) => a + s.dur, 0);

export const sceneAt = (t: number) => {
  for (let i = SCENES.length - 1; i >= 0; i--) if (t >= SCENES[i].start) return i;
  return 0;
};

/** Instante "pose" de cada cena: tudo já entrou e nada começou a sair (usado nas imagens). */
export const snapshotTime = (i: number) => SCENES[i].start + Math.min(SCENES[i].dur - 0.45, 2.1);

// ── Efeitos sonoros sincronizados com as cenas ──
export interface SfxCue { t: number; name: SfxName; gain: number }
export const SFX_CUES: SfxCue[] = (() => {
  const cues: SfxCue[] = [{ t: 0.15, name: 'chime', gain: 0.55 }];
  SCENES.forEach((s, i) => {
    if (i > 0) cues.push({ t: Math.max(0, s.start - 0.18), name: 'whoosh', gain: 0.5 });
    if (['days', 'words', 'time'].includes(s.id)) {
      for (let k = 0; k < 6; k++) cues.push({ t: s.start + 0.3 + k * 0.09, name: 'tick', gain: 0.35 });
      cues.push({ t: s.start + 0.9, name: 'pop', gain: 0.6 });
    }
    if (s.id === 'activities') for (let k = 0; k < 6; k++) cues.push({ t: s.start + 0.45 + k * 0.12, name: 'pop', gain: 0.32 });
    if (s.id === 'skills') for (let k = 0; k < 6; k++) cues.push({ t: s.start + 0.5 + k * 0.1, name: 'tick', gain: 0.3 });
    if (s.id === 'quality') cues.push({ t: s.start + 1.0, name: 'pop', gain: 0.55 });
    if (s.id === 'mastery') cues.push({ t: s.start + 0.45, name: 'sparkle', gain: 0.6 });
    if (s.id === 'outro') { cues.push({ t: s.start - 1.5, name: 'rise', gain: 0.45 }); cues.push({ t: s.start + 0.1, name: 'chime', gain: 0.6 }); }
  });
  return cues.sort((a, b) => a.t - b.t);
})();

// ── Utilidades de animação ────────────────────────────────────────
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const prog = (t: number, a: number, b: number) => clamp01((t - a) / (b - a));
const easeOut = (x: number) => 1 - Math.pow(1 - x, 3);
const easeInOut = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const easeBack = (x: number) => { const c1 = 1.70158, c3 = c1 + 1; return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2); };
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
const hexToRgb = (h: string) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const mixHex = (a: string, b: string, k: number) => {
  const A = hexToRgb(a), B = hexToRgb(b);
  return `rgb(${Math.round(lerp(A[0], B[0], k))},${Math.round(lerp(A[1], B[1], k))},${Math.round(lerp(A[2], B[2], k))})`;
};
const rgba = (hex: string, a: number) => { const [r, g, b] = hexToRgb(hex); return `rgba(${r},${g},${b},${a})`; };
const fmt = (n: number) => Math.round(n).toLocaleString('pt-BR');
const seeded = (seed: number) => () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };

/** Entrada/saída de um elemento dentro da cena: sobe e aparece; no fim, sobe um pouco e some. */
const appear = (lt: number, dur: number, delay = 0) => {
  const e = easeOut(prog(lt, delay, delay + 0.55));
  const x = easeInOut(prog(lt, dur - 0.32, dur));
  return { a: e * (1 - x), dy: (1 - e) * 70 - x * 50 };
};

// ── Texto ─────────────────────────────────────────────────────────
interface TextOpts { size: number; weight?: number; color?: string; align?: CanvasTextAlign; mono?: boolean; spacing?: number; alpha?: number; maxWidth?: number; baseline?: CanvasTextBaseline }

const setFont = (ctx: CanvasRenderingContext2D, size: number, weight = 400, mono = false) => {
  ctx.font = `${weight} ${size}px ${mono ? "'Geist Mono', ui-monospace, monospace" : "'Geist', 'Inter', system-ui, sans-serif"}`;
};

const text = (ctx: CanvasRenderingContext2D, str: string, x: number, y: number, o: TextOpts) => {
  ctx.save();
  let size = o.size;
  setFont(ctx, size, o.weight, o.mono);
  const anyCtx = ctx as any;
  if ('letterSpacing' in anyCtx) anyCtx.letterSpacing = `${(o.spacing ?? 0) * size}px`;
  if (o.maxWidth) {
    while (ctx.measureText(str).width > o.maxWidth && size > 18) {
      size -= 4;
      setFont(ctx, size, o.weight, o.mono);
      if ('letterSpacing' in anyCtx) anyCtx.letterSpacing = `${(o.spacing ?? 0) * size}px`;
    }
  }
  ctx.globalAlpha *= o.alpha ?? 1;
  ctx.fillStyle = o.color || C.ink;
  ctx.textAlign = o.align || 'center';
  ctx.textBaseline = o.baseline || 'alphabetic';
  ctx.fillText(str, x, y);
  const w = ctx.measureText(str).width;
  ctx.restore();
  return w;
};

/** Título editorial com ponto final na cor de destaque (como no app) */
const titleDot = (ctx: CanvasRenderingContext2D, str: string, x: number, y: number, size: number, alpha: number, maxWidth = W - 160) => {
  ctx.save();
  setFont(ctx, size, 500);
  const anyCtx = ctx as any;
  if ('letterSpacing' in anyCtx) anyCtx.letterSpacing = `${-0.035 * size}px`;
  let s = size;
  while (ctx.measureText(str + '.').width > maxWidth && s > 30) {
    s -= 4; setFont(ctx, s, 500);
    if ('letterSpacing' in anyCtx) anyCtx.letterSpacing = `${-0.035 * s}px`;
  }
  const w = ctx.measureText(str).width;
  const dotW = ctx.measureText('.').width;
  const left = x - (w + dotW) / 2;
  ctx.globalAlpha *= alpha;
  ctx.textAlign = 'left';
  ctx.fillStyle = C.ink;
  ctx.fillText(str, left, y);
  ctx.fillStyle = C.primary;
  ctx.fillText('.', left + w, y);
  ctx.restore();
};

const label = (ctx: CanvasRenderingContext2D, str: string, y: number, alpha: number, color = C.muted) =>
  text(ctx, str.toUpperCase(), W / 2, y, { size: 30, mono: true, spacing: 0.16, color, alpha });

const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const iconDisc = (ctx: CanvasRenderingContext2D, assets: WrappedAssets, key: string, cx: number, cy: number, r: number, bg: string, variant: 'dark' | 'light', alpha: number, scale = 1) => {
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.translate(cx, cy);
  ctx.scale(scale, scale);
  ctx.fillStyle = bg;
  ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill();
  const img = assets.icons[`${key}-${variant}`];
  if (img && img.complete && img.naturalWidth) {
    const s = r * 1.05;
    ctx.drawImage(img, -s / 2, -s / 2, s, s);
  }
  ctx.restore();
};

const ring = (ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, width: number, pct: number, color: string, alpha: number) => {
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.lineCap = 'round';
  ctx.lineWidth = width;
  ctx.strokeStyle = 'rgba(243,239,232,0.08)';
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
  if (pct > 0.001) {
    ctx.strokeStyle = color;
    ctx.beginPath(); ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.min(1, pct)); ctx.stroke();
  }
  ctx.restore();
};

const pill = (ctx: CanvasRenderingContext2D, cx: number, cy: number, str: string, bg: string, color: string, alpha: number, size = 34, iconKey?: string, assets?: WrappedAssets) => {
  ctx.save();
  setFont(ctx, size, 400);
  const tw = ctx.measureText(str).width;
  const iconW = iconKey ? size * 1.5 : 0;
  const w = tw + size * 1.6 + iconW;
  const h = size * 2.1;
  ctx.globalAlpha *= alpha;
  ctx.fillStyle = bg;
  roundRect(ctx, cx - w / 2, cy - h / 2, w, h, h / 2);
  ctx.fill();
  if (iconKey && assets) {
    const img = assets.icons[`${iconKey}-light`];
    if (img && img.naturalWidth) ctx.drawImage(img, cx - w / 2 + size * 0.8, cy - size * 0.6, size * 1.2, size * 1.2);
  }
  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(str, cx - w / 2 + size * 0.8 + iconW, cy + 2);
  ctx.restore();
};

// ── Fundo: grafite com dois brilhos pastel que mudam de cor entre as cenas ──
const background = (ctx: CanvasRenderingContext2D, t: number) => {
  const i = sceneAt(t);
  const s = SCENES[i];
  const next = SCENES[Math.min(SCENES.length - 1, i + 1)];
  const k = easeInOut(prog(t, s.start + s.dur - 0.5, s.start + s.dur));
  const c1 = mixHex(LIGHT[s.tint], LIGHT[next.tint], k);
  const c2 = mixHex(LIGHT[SCENES[(i + 2) % SCENES.length].tint], LIGHT[SCENES[(i + 3) % SCENES.length].tint], k);

  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, H);

  const blob = (x: number, y: number, r: number, color: string, a: number) => {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, color.replace('rgb(', 'rgba(').replace(')', `,${a})`));
    g.addColorStop(1, 'rgba(20,20,19,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  };
  blob(W * 0.82 + Math.sin(t * 0.35) * 90, H * 0.16 + Math.cos(t * 0.28) * 70, 980, c1, 0.3);
  blob(W * 0.12 + Math.cos(t * 0.3) * 80, H * 0.86 + Math.sin(t * 0.25) * 60, 860, c2, 0.16);

  // grade de pontos bem sutil (toque futurista)
  ctx.fillStyle = 'rgba(243,239,232,0.045)';
  for (let y = 60; y < H; y += 60) for (let x = 60; x < W; x += 60) ctx.fillRect(x, y, 2.4, 2.4);

  // anel que se expande na troca de cena
  const lt = t - s.start;
  if (i > 0 && lt < 0.9) {
    const p = easeOut(lt / 0.9);
    ctx.save();
    ctx.strokeStyle = rgba(LIGHT[s.tint], 0.35 * (1 - p));
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(W / 2, H / 2, 120 + p * 900, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }
};

const logo = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number, alpha: number, align: 'right' | 'center' = 'right') => {
  ctx.save();
  setFont(ctx, size, 600);
  const anyCtx = ctx as any;
  if ('letterSpacing' in anyCtx) anyCtx.letterSpacing = `${-0.04 * size}px`;
  const w = ctx.measureText('libritui').width;
  const dw = ctx.measureText('.').width;
  const left = align === 'right' ? x - w - dw : x - (w + dw) / 2;
  ctx.globalAlpha *= alpha;
  ctx.textAlign = 'left';
  ctx.fillStyle = C.ink;
  ctx.fillText('libritui', left, y);
  ctx.fillStyle = C.primary;
  ctx.fillText('.', left + w, y);
  ctx.restore();
};

const PERIOD_NAME: Record<WrappedPeriod, string> = { daily: 'do dia', weekly: 'da semana', monthly: 'do mês', yearly: 'do ano' };
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const dateRange = (p: WrappedPeriod) => {
  const end = new Date();
  const f = (d: Date) => `${d.getDate()} de ${MONTHS[d.getMonth()]}`;
  if (p === 'daily') return f(end);
  if (p === 'yearly') return `${end.getFullYear()}`;
  const start = new Date(end);
  start.setDate(end.getDate() - (p === 'weekly' ? 6 : 29));
  return `${f(start)} a ${f(end)}`;
};

// ── Cenas ─────────────────────────────────────────────────────────
type Draw = (ctx: CanvasRenderingContext2D, lt: number, dur: number, d: WrappedData, a: WrappedAssets, t: number) => void;

const drawIntro: Draw = (ctx, lt, dur, d, assets, t) => {
  const av = appear(lt, dur, 0.05);
  const cx = W / 2, cy = 700, r = 190;
  const s = easeBack(prog(lt, 0.05, 0.75));
  ctx.save();
  ctx.globalAlpha = av.a;
  ctx.translate(cx, cy + av.dy);
  ctx.scale(0.6 + 0.4 * s, 0.6 + 0.4 * s);
  // anel orbital (gradiente cônico girando)
  const anyCtx = ctx as any;
  if (anyCtx.createConicGradient) {
    const g = anyCtx.createConicGradient(t * 0.9, 0, 0);
    g.addColorStop(0, C.primary); g.addColorStop(0.35, 'rgba(196,120,82,0)'); g.addColorStop(0.6, rgba(LIGHT.lavender, 0.7)); g.addColorStop(0.85, 'rgba(196,120,82,0)'); g.addColorStop(1, C.primary);
    ctx.strokeStyle = g;
  } else ctx.strokeStyle = C.primary;
  ctx.lineWidth = 10;
  ctx.beginPath(); ctx.arc(0, 0, r + 26, 0, Math.PI * 2); ctx.stroke();
  // avatar
  ctx.save();
  ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.clip();
  if (assets.avatar && assets.avatar.naturalWidth) ctx.drawImage(assets.avatar, -r, -r, r * 2, r * 2);
  else {
    ctx.fillStyle = '#b0805f'; ctx.fillRect(-r, -r, r * 2, r * 2);
    text(ctx, (d.displayName || d.username || 'L').slice(0, 1).toUpperCase(), 0, 60, { size: 180, weight: 400 });
  }
  ctx.restore();
  ctx.restore();

  const a1 = appear(lt, dur, 0.35), a2 = appear(lt, dur, 0.5), a3 = appear(lt, dur, 0.65);
  ctx.save(); ctx.translate(0, a1.dy); label(ctx, 'sua retrospectiva', 1060, a1.a, C.primary); ctx.restore();
  ctx.save(); ctx.translate(0, a2.dy); titleDot(ctx, `Resumo ${PERIOD_NAME[d.period]}`, W / 2, 1200, 124, a2.a); ctx.restore();
  ctx.save(); ctx.translate(0, a3.dy);
  text(ctx, `@${d.username}`, W / 2, 1320, { size: 44, mono: true, color: C.ink, alpha: a3.a * 0.85 });
  text(ctx, dateRange(d.period), W / 2, 1390, { size: 38, color: C.muted, alpha: a3.a });
  ctx.restore();
};

const bigNumberScene = (ctx: CanvasRenderingContext2D, lt: number, dur: number, labelStr: string, value: number, prefix: string, caption: string, tint: Tint) => {
  const a0 = appear(lt, dur, 0), a1 = appear(lt, dur, 0.15), a2 = appear(lt, dur, 0.4);
  ctx.save(); ctx.translate(0, a0.dy); label(ctx, labelStr, 380, a0.a, LIGHT[tint]); ctx.restore();
  const n = value * easeOut(prog(lt, 0.25, 0.95));
  const pop = 1 + 0.06 * Math.sin(Math.PI * prog(lt, 0.9, 1.25));
  ctx.save();
  ctx.translate(W / 2, 780 + a1.dy);
  ctx.scale(pop, pop);
  text(ctx, `${prefix}${fmt(n)}`, 0, 0, { size: 330, weight: 300, spacing: -0.06, alpha: a1.a, maxWidth: W - 120 });
  ctx.restore();
  ctx.save(); ctx.translate(0, a2.dy); text(ctx, caption, W / 2, 900, { size: 54, color: C.ink, alpha: a2.a * 0.8, maxWidth: W - 160 }); ctx.restore();
};

const drawDays: Draw = (ctx, lt, dur, d) => {
  bigNumberScene(ctx, lt, dur, 'constância', d.studyDays, '', d.studyDays === 1 ? 'dia de estudo ativo' : 'dias de estudo ativo', 'peach');
  const total = Math.max(1, Math.min(d.period === 'yearly' ? 12 : d.periodDays, 30));
  const cols = total <= 7 ? total : total <= 12 ? 6 : 10;
  const size = total <= 7 ? 92 : 64, gap = total <= 7 ? 28 : 22;
  const rows = Math.ceil(total / cols);
  const gw = cols * size + (cols - 1) * gap;
  const x0 = (W - gw) / 2, y0 = 1080;
  const lit = Math.min(total, d.period === 'yearly' ? Math.round((d.consistency / 100) * 12) : d.studyDays);
  for (let k = 0; k < total; k++) {
    const r = Math.floor(k / cols), c = k % cols;
    const ap = appear(lt, dur, 0.5 + k * 0.025);
    const on = k < lit && lt > 0.9 + k * 0.06;
    const sc = on ? easeBack(prog(lt, 0.9 + k * 0.06, 1.25 + k * 0.06)) : 1;
    const cx = x0 + c * (size + gap) + size / 2, cy = y0 + r * (size + gap) + size / 2 + ap.dy;
    ctx.save();
    ctx.globalAlpha = ap.a;
    ctx.translate(cx, cy); ctx.scale(on ? 0.85 + 0.15 * sc : 1, on ? 0.85 + 0.15 * sc : 1);
    ctx.fillStyle = on ? LIGHT.peach : 'rgba(243,239,232,0.1)';
    ctx.beginPath(); ctx.arc(0, 0, size / 2, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
  const ap = appear(lt, dur, 0.8);
  ctx.save(); ctx.translate(0, ap.dy);
  pill(ctx, W / 2, y0 + rows * (size + gap) + 90, `consistência de ${Math.round(d.consistency)}%`, DEEP.peach, C.ink, ap.a, 34);
  ctx.restore();
};

const drawWords: Draw = (ctx, lt, dur, d, assets) => {
  bigNumberScene(ctx, lt, dur, 'vocabulário', d.wordsLearned, '+', d.wordsLearned === 1 ? 'palavra nova no repertório' : 'palavras novas no repertório', 'sky');
  // palavras dominadas flutuando
  const words = d.masteredWords.slice(0, 6);
  const rnd = seeded(42);
  words.forEach((w, k) => {
    const ap = appear(lt, dur, 0.55 + k * 0.08);
    // grade 3 x 2 com deslocamento leve: as palavras nunca se sobrepõem
    const col = k % 3, row = Math.floor(k / 3);
    const x = W / 2 + (col - 1) * 310 + (row % 2 ? 70 : -40) + (rnd() - 0.5) * 30;
    const y = 1070 + row * 150 + (rnd() - 0.5) * 40 - lt * 14;
    ctx.save(); ctx.translate(0, ap.dy);
    pill(ctx, x, y, w.length > 18 ? `${w.slice(0, 17)}…` : w, DEEP.sky, C.ink, ap.a * 0.95, 32);
    ctx.restore();
  });
  const ap = appear(lt, dur, 0.7);
  const y = words.length ? 1500 : 1150;
  ctx.save(); ctx.translate(0, ap.dy);
  ctx.globalAlpha = ap.a;
  ctx.fillStyle = 'rgba(243,239,232,0.06)';
  roundRect(ctx, 150, y - 90, W - 300, 180, 90); ctx.fill();
  iconDisc(ctx, assets, 'words', 260, y, 56, LIGHT.sky, 'dark', 1);
  text(ctx, 'total no repertório', 350, y - 14, { size: 30, mono: true, spacing: 0.1, color: C.muted, align: 'left' });
  text(ctx, `${fmt(d.totalVocab)} palavras`, 350, y + 44, { size: 52, weight: 500, align: 'left', maxWidth: W - 560 });
  ctx.restore();
};

const drawTime: Draw = (ctx, lt, dur, d, assets) => {
  const a0 = appear(lt, dur, 0);
  ctx.save(); ctx.translate(0, a0.dy); label(ctx, 'tempo de prática', 330, a0.a, LIGHT.sage); ctx.restore();
  const goal = Math.max(30, (d.period === 'daily' ? 1 : d.period === 'weekly' ? 7 : d.period === 'monthly' ? 30 : 365) * 15);
  const ar = appear(lt, dur, 0.1);
  const p = easeOut(prog(lt, 0.25, 1.3));
  ring(ctx, W / 2, 800 + ar.dy, 300, 30, (d.minutes / goal) * p, LIGHT.sage, ar.a);
  ctx.save(); ctx.translate(0, ar.dy);
  text(ctx, fmt(d.minutes * easeOut(prog(lt, 0.25, 0.95))), W / 2, 850, { size: 220, weight: 300, spacing: -0.06, alpha: ar.a });
  text(ctx, 'minutos', W / 2, 930, { size: 40, mono: true, spacing: 0.1, color: C.muted, alpha: ar.a });
  ctx.restore();
  const h = Math.floor(d.minutes / 60), m = d.minutes % 60;
  const a2 = appear(lt, dur, 0.45);
  ctx.save(); ctx.translate(0, a2.dy);
  text(ctx, h > 0 ? `≈ ${h}h${m ? ` ${m}min` : ''} dedicados ao idioma` : 'dedicados ao idioma', W / 2, 1230, { size: 50, alpha: a2.a * 0.85, maxWidth: W - 160 });
  ctx.restore();
  const a3 = appear(lt, dur, 0.65);
  ctx.save(); ctx.translate(0, a3.dy);
  pill(ctx, W / 2 - 200, 1360, `${fmt(d.voiceMinutes)} min de voz`, DEEP.sage, C.ink, a3.a, 32, 'active_voice', assets);
  pill(ctx, W / 2 + 210, 1360, `${fmt(d.pages)} págs lidas`, DEEP.sage, C.ink, a3.a, 32, 'reading', assets);
  ctx.restore();
};

const ACTIVITY_ITEMS: { key: string; field: keyof WrappedData; unit: string; tint: Tint }[] = [
  { key: 'flashcards', field: 'cards', unit: 'cards revisados', tint: 'peach' },
  { key: 'reading', field: 'pages', unit: 'páginas lidas', tint: 'lavender' },
  { key: 'writing', field: 'writing', unit: 'textos escritos', tint: 'rose' },
  { key: 'pronunciation', field: 'pronunciation', unit: 'áudios de pronúncia', tint: 'sky' },
  { key: 'grammar', field: 'quizzes', unit: 'quizzes de gramática', tint: 'sage' },
  { key: 'listening', field: 'listening', unit: 'exercícios de listening', tint: 'butter' },
];

const drawActivities: Draw = (ctx, lt, dur, d, assets) => {
  const a0 = appear(lt, dur, 0), a1 = appear(lt, dur, 0.1);
  ctx.save(); ctx.translate(0, a0.dy); label(ctx, 'o que você praticou', 300, a0.a, LIGHT.butter); ctx.restore();
  ctx.save(); ctx.translate(0, a1.dy); titleDot(ctx, 'Seis frentes de estudo', W / 2, 420, 82, a1.a); ctx.restore();
  const cw = 420, ch = 330, gx = 40, gy = 36;
  const x0 = (W - (cw * 2 + gx)) / 2, y0 = 520;
  ACTIVITY_ITEMS.forEach((it, k) => {
    const c = k % 2, r = Math.floor(k / 2);
    const ap = appear(lt, dur, 0.4 + k * 0.12);
    const sc = 0.9 + 0.1 * easeBack(prog(lt, 0.4 + k * 0.12, 0.9 + k * 0.12));
    const x = x0 + c * (cw + gx), y = y0 + r * (ch + gy) + ap.dy;
    ctx.save();
    ctx.globalAlpha = ap.a;
    ctx.translate(x + cw / 2, y + ch / 2); ctx.scale(sc, sc); ctx.translate(-cw / 2, -ch / 2);
    ctx.fillStyle = DEEP[it.tint];
    roundRect(ctx, 0, 0, cw, ch, 48); ctx.fill();
    iconDisc(ctx, assets, it.key, 88, 88, 48, LIGHT[it.tint], 'dark', 1);
    const v = Number(d[it.field]) || 0;
    text(ctx, fmt(v * easeOut(prog(lt, 0.55 + k * 0.12, 1.4 + k * 0.12))), 44, 236, { size: 104, weight: 300, spacing: -0.05, align: 'left', maxWidth: cw - 80 });
    text(ctx, it.unit, 44, 290, { size: 28, mono: true, spacing: 0.02, color: rgba(C.ink, 0.6), align: 'left', maxWidth: cw - 80 });
    ctx.restore();
  });
};

const SKILL_TINT: Record<SkillKey, Tint> = { reading: 'lavender', flashcards: 'sky', active_voice: 'sage', writing: 'rose', pronunciation: 'peach', listening: 'butter' };

const drawSkills: Draw = (ctx, lt, dur, d, assets) => {
  const a0 = appear(lt, dur, 0), a1 = appear(lt, dur, 0.1);
  ctx.save(); ctx.translate(0, a0.dy); label(ctx, 'equilíbrio de habilidades', 300, a0.a, LIGHT.lavender); ctx.restore();
  const top = [...d.skills].sort((a, b) => b.pct - a.pct)[0];
  ctx.save(); ctx.translate(0, a1.dy); titleDot(ctx, 'Seu perfil de estudo', W / 2, 420, 82, a1.a); ctx.restore();
  const rowH = 150, y0 = 560, x0 = 110, bw = W - 220;
  d.skills.forEach((s, k) => {
    const ap = appear(lt, dur, 0.35 + k * 0.1);
    const y = y0 + k * rowH + ap.dy;
    const p = easeOut(prog(lt, 0.5 + k * 0.1, 1.5 + k * 0.1));
    ctx.save();
    ctx.globalAlpha = ap.a;
    iconDisc(ctx, assets, s.key, x0 + 42, y + 30, 42, LIGHT[SKILL_TINT[s.key]], 'dark', 1);
    text(ctx, s.name, x0 + 110, y + 22, { size: 38, align: 'left', maxWidth: bw - 300 });
    text(ctx, `${Math.round(s.pct * p)}%`, x0 + bw, y + 24, { size: 46, weight: 300, align: 'right' });
    ctx.fillStyle = 'rgba(243,239,232,0.08)';
    roundRect(ctx, x0 + 110, y + 52, bw - 110, 14, 7); ctx.fill();
    if (s.pct > 0) {
      ctx.fillStyle = LIGHT[SKILL_TINT[s.key]];
      roundRect(ctx, x0 + 110, y + 52, Math.max(14, (bw - 110) * (s.pct / 100) * p), 14, 7); ctx.fill();
    }
    ctx.restore();
  });
  const ap = appear(lt, dur, 1.1);
  if (top && top.pct > 0) {
    ctx.save(); ctx.translate(0, ap.dy);
    pill(ctx, W / 2, y0 + d.skills.length * rowH + 70, `ponto forte: ${top.name.toLowerCase()}`, DEEP.lavender, C.ink, ap.a, 34, top.key, assets);
    ctx.restore();
  }
};

const drawQuality: Draw = (ctx, lt, dur, d, assets) => {
  const a0 = appear(lt, dur, 0), a1 = appear(lt, dur, 0.1);
  ctx.save(); ctx.translate(0, a0.dy); label(ctx, 'qualidade', 360, a0.a, LIGHT.rose); ctx.restore();
  ctx.save(); ctx.translate(0, a1.dy);
  titleDot(ctx, d.accuracy >= 80 ? 'Precisão de mestre' : 'Cada erro é um degrau', W / 2, 480, 80, a1.a);
  ctx.restore();
  const items = [
    { v: d.accuracy, name: 'precisão', tint: 'sage' as Tint, icon: 'accuracy', x: W / 2 - 230 },
    { v: d.consistency, name: 'consistência', tint: 'rose' as Tint, icon: 'consistency', x: W / 2 + 230 },
  ];
  items.forEach((it, k) => {
    const ap = appear(lt, dur, 0.3 + k * 0.15);
    const p = easeOut(prog(lt, 0.45 + k * 0.15, 1.5 + k * 0.15));
    const cy = 860 + ap.dy;
    ring(ctx, it.x, cy, 175, 24, (it.v / 100) * p, LIGHT[it.tint], ap.a);
    text(ctx, `${Math.round(it.v * p)}%`, it.x, cy + 30, { size: 100, weight: 300, spacing: -0.05, alpha: ap.a });
    iconDisc(ctx, assets, it.icon, it.x, cy + 300, 44, LIGHT[it.tint], 'dark', ap.a);
    text(ctx, it.name, it.x, cy + 400, { size: 34, mono: true, spacing: 0.1, color: C.muted, alpha: ap.a });
  });
  const ap = appear(lt, dur, 0.8);
  ctx.save(); ctx.translate(0, ap.dy);
  text(ctx, `${d.studyDays} ${d.studyDays === 1 ? 'dia ativo' : 'dias ativos'} no período`, W / 2, 1460, { size: 44, alpha: ap.a * 0.75 });
  ctx.restore();
};

const drawMastery: Draw = (ctx, lt, dur, d, assets) => {
  const a0 = appear(lt, dur, 0), a1 = appear(lt, dur, 0.15), a2 = appear(lt, dur, 0.4);
  ctx.save(); ctx.translate(0, a0.dy); label(ctx, 'hall da maestria', 440, a0.a, LIGHT.peach); ctx.restore();
  iconDisc(ctx, assets, 'trophy', W / 2, 640 + a0.dy, 92, LIGHT.butter, 'dark', a0.a, 0.7 + 0.3 * easeBack(prog(lt, 0, 0.6)));
  const word = d.masteredWords[0];
  // brilhos ao redor da palavra
  if (word) {
    const rnd = seeded(7);
    const p = prog(lt, 0.4, 1.8);
    for (let k = 0; k < 26; k++) {
      const ang = rnd() * Math.PI * 2, dist = 160 + rnd() * 360;
      const x = W / 2 + Math.cos(ang) * dist * easeOut(p), y = 960 + Math.sin(ang) * dist * 0.6 * easeOut(p);
      ctx.save();
      ctx.globalAlpha = (1 - p) * a1.a * 0.9;
      ctx.fillStyle = k % 3 === 0 ? C.primary : LIGHT.butter;
      ctx.beginPath(); ctx.arc(x, y, 4 + rnd() * 6, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
  }
  ctx.save(); ctx.translate(0, a1.dy);
  text(ctx, word ? `“${word}”` : 'Nenhum erro pendente', W / 2, 1000, { size: word ? 140 : 90, weight: 400, spacing: -0.04, alpha: a1.a, maxWidth: W - 140 });
  ctx.restore();
  ctx.save(); ctx.translate(0, a2.dy);
  text(ctx, word ? 'superada e dominada com sucesso' : 'seu histórico está limpo', W / 2, 1110, { size: 46, color: C.ink, alpha: a2.a * 0.7 });
  if (d.masteredWords.length > 1) text(ctx, `+${d.masteredWords.length - 1} outras conquistas no período`, W / 2, 1190, { size: 34, mono: true, color: C.muted, alpha: a2.a });
  ctx.restore();
};

const drawOutro: Draw = (ctx, lt, dur, d) => {
  const s = easeBack(prog(lt, 0.05, 0.8));
  const a0 = clamp01(lt / 0.5);
  ctx.save();
  ctx.translate(W / 2, 760);
  ctx.scale(0.7 + 0.3 * s, 0.7 + 0.3 * s);
  logo(ctx, 0, 0, 190, a0, 'center');
  ctx.restore();
  const a1 = appear(lt, dur + 10, 0.4), a2 = appear(lt, dur + 10, 0.6), a3 = appear(lt, dur + 10, 0.85);
  ctx.save(); ctx.translate(0, a1.dy);
  text(ctx, `Parabéns pela evolução, @${d.username}.`, W / 2, 920, { size: 46, alpha: a1.a * 0.85, maxWidth: W - 160 });
  ctx.restore();
  const stats = [
    { v: d.wordsLearned, l: 'palavras' },
    { v: d.minutes, l: 'minutos' },
    { v: d.studyDays, l: d.studyDays === 1 ? 'dia' : 'dias' },
  ];
  ctx.save(); ctx.translate(0, a2.dy);
  stats.forEach((st, k) => {
    const x = W / 2 + (k - 1) * 300;
    text(ctx, fmt(st.v), x, 1150, { size: 110, weight: 300, spacing: -0.05, alpha: a2.a });
    text(ctx, st.l, x, 1215, { size: 30, mono: true, spacing: 0.12, color: C.muted, alpha: a2.a });
    if (k > 0) { ctx.save(); ctx.globalAlpha = a2.a * 0.15; ctx.fillStyle = C.ink; ctx.fillRect(x - 150, 1060, 2, 170); ctx.restore(); }
  });
  ctx.restore();
  ctx.save(); ctx.translate(0, a3.dy);
  text(ctx, 'continue lendo. continue aprendendo.', W / 2, 1420, { size: 36, mono: true, spacing: 0.04, color: C.muted, alpha: a3.a });
  ctx.restore();
};

const DRAW: Record<SceneId, Draw> = {
  intro: drawIntro, days: drawDays, words: drawWords, time: drawTime, activities: drawActivities,
  skills: drawSkills, quality: drawQuality, mastery: drawMastery, outro: drawOutro,
};

/** Desenha o quadro do instante t (segundos). Mesma função para o player, o vídeo e as imagens. */
export const drawFrame = (ctx: CanvasRenderingContext2D, t: number, data: WrappedData, assets: WrappedAssets) => {
  const tt = Math.max(0, Math.min(TOTAL_DURATION - 0.001, t));
  ctx.save();
  ctx.setTransform(ctx.canvas.width / W, 0, 0, ctx.canvas.height / H, 0, 0);
  background(ctx, tt);
  const i = sceneAt(tt);
  const s = SCENES[i];
  DRAW[s.id](ctx, tt - s.start, i === SCENES.length - 1 ? s.dur + 10 : s.dur, data, assets, tt);
  if (s.id !== 'outro') logo(ctx, W - 72, H - 84, 46, 0.92);
  ctx.restore();
};

// ── Recursos: fontes, ícones (os mesmos do app) e avatar ─────────
const ICONS: Record<string, React.FC<any>> = {
  flashcards: Brain, reading: BookOpenText, writing: PenLine, pronunciation: Mic2, grammar: BookOpen,
  listening: Headphones, active_voice: Mic, words: WholeWord, time: Clock, consistency: Flame,
  accuracy: Crosshair, trophy: Trophy, sparkles: Sparkles, calendar: CalendarDays, library: Library,
};

const loadImage = (src: string, crossOrigin = false) => new Promise<HTMLImageElement | null>(resolve => {
  const img = new Image();
  if (crossOrigin) img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

export const loadWrappedAssets = async (avatarUrl?: string): Promise<WrappedAssets> => {
  const { renderToStaticMarkup } = await import('react-dom/server');
  await Promise.all([
    document.fonts?.load("300 100px 'Geist'"), document.fonts?.load("400 100px 'Geist'"),
    document.fonts?.load("500 100px 'Geist'"), document.fonts?.load("600 100px 'Geist'"),
    document.fonts?.load("400 40px 'Geist Mono'"),
  ].map(p => p?.catch(() => null)));

  const icons: Record<string, HTMLImageElement> = {};
  await Promise.all(Object.entries(ICONS).flatMap(([key, Icon]) =>
    (['dark', 'light'] as const).map(async variant => {
      const svg = renderToStaticMarkup(React.createElement(Icon, { size: 96, strokeWidth: 1.6, color: variant === 'dark' ? C.dark : C.ink }));
      const img = await loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
      if (img) icons[`${key}-${variant}`] = img;
    })
  ));

  // crossOrigin evita "sujar" o canvas (senão o vídeo e as imagens não podem ser exportados)
  const avatar = avatarUrl ? await loadImage(avatarUrl, true) : null;
  return { avatar, icons };
};
